import { SeatingOccasions } from '@maverick/entity';
import { useInput } from '@maverick/hooks';
import { Checkbox, DropdownItem, Holder, Icon, IconMapPin } from '@maverick/ui';
import { CmsInputModes, InputCms, ValidationManager, checkIsEmailValid } from '@maverick/cms';
import { CmsDropdown } from 'components/cms/CmsDropdown/CmsDropdown';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { GuestInfoFormValidator } from './GuestInfoForm.validator';
import { SeatingManager } from 'src/manager/Seating.manager';
import { SeatingModal } from '../SeatingModal';
import { useRouter } from 'next/router';
import * as Styled from '../Seating.styled';
import { GaEvent, handleFormatGuestsAndTimeSelections } from '@maverick/utils';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { SeatingStepsReduxProps } from '.';

const occasionOptions = [
	{ value: SeatingOccasions.None, text: SeatingOccasions.Label(SeatingOccasions.None) },
	{ value: SeatingOccasions.Birthday, text: SeatingOccasions.Label(SeatingOccasions.Birthday) },
	{ value: SeatingOccasions.Anniversary, text: SeatingOccasions.Label(SeatingOccasions.Anniversary) },
	{ value: SeatingOccasions.Graduation, text: SeatingOccasions.Label(SeatingOccasions.Graduation) },
	{ value: SeatingOccasions.Other, text: SeatingOccasions.Label(SeatingOccasions.Other) },
];

interface GuestInfoFormProps extends SeatingStepsReduxProps {
	onBackClick: () => void;
	setLoading: (loading: boolean) => void;
	onSuccess: () => void;
}

export const GuestInfoForm: FC<GuestInfoFormProps> = ({
	seatingData,
	setSeatingData,
	onBackClick,
	setLoading,
	onSuccess,
}) => {
	const firstName = useInput(seatingData.FirstName, { label: 'First name' });
	const lastName = useInput(seatingData.LastName, { label: 'Last name' });
	const phoneNumber = useInput(seatingData.PhoneNumber, { label: 'Phone number', maxlength: 10 });
	const email = useInput(seatingData.Email, { label: 'Email address' });
	const occasion = useInput(seatingData.Occasion ?? occasionOptions[0].value, {
		label: 'Special occasion?',
	});
	const [checkbox, setCheckbox] = useState<boolean>(true);

	const [error, setError] = useState<boolean>(false);
	const [unavailableTimeSlot, setUnavailableTimeSlot] = useState<boolean>(false);
	const [timeSlots, setTimeSlots] = useState<Array<DropdownItem>>([]);
	const timeSlot = useInput('');

	const router = useRouter();

	const [submitError, setSubmitError] = useState<string>('');

	const { sitecoreContext } = useSitecoreContext();

	const checkProfanity = async () => {
		const checkResult = await ValidationManager.ProfanityCheck(firstName.value, lastName.value);
		let isValid: boolean = true;

		if (checkResult == null) {
			setError(true);

			GaEvent.ErrorView(
				'Validation service is unavailable at the moment. Please try again at a later time.',
				sitecoreContext.route?.displayName
			);

			return false;
		}

		if (!checkResult.first) {
			firstName.setError('Valid first name required');
			isValid = false;
		}

		if (!checkResult.last) {
			lastName.setError('Valid last name required');
			isValid = false;
		}

		return isValid;
	};

	const checkEmailDomain = async () => {
		const invalidDomains = await ValidationManager.GetInvalidEmailExtensions();

		return checkIsEmailValid(email.value, invalidDomains);
	};

	const handleConfirm = async () => {
		const valid = GuestInfoFormValidator(firstName, lastName, phoneNumber, email);
		if (!valid) return;

		seatingData.FirstName = firstName.value;
		seatingData.LastName = lastName.value;
		seatingData.PhoneNumber = phoneNumber.value;
		seatingData.Email = email.value;
		seatingData.Occasion = occasion.value;
		seatingData.SpecialOccasion = SeatingOccasions.Label(occasion.value);

		setLoading(true);

		if (!(await checkProfanity())) {
			setLoading(false);
			return;
		}

		if (!(await checkEmailDomain())) {
			email.setError('Valid email address required');
			setLoading(false);
			return;
		}

		const result = await SeatingManager.GetAvailableTimeSlots(seatingData);
		setLoading(false);

		if (!result) {
			setError(true);

			GaEvent.ErrorView(
				'Wait list service is unavailable at the moment. Please try again at a later time.',
				sitecoreContext.route?.displayName
			);

			return;
		}

		if (!result.includes(seatingData.TimeSlot)) {
			setUnavailableTimeSlot(true);
			setTimeSlots(result.map((r) => ({ value: r, text: r })));

			return;
		}

		submit();
	};

	const submit = async () => {
		setLoading(true);
		const { error, partyKey } = await SeatingManager.CreateReservation(seatingData);
		setLoading(false);

		if (!!error) {
			setSubmitError(error);
			GaEvent.ErrorView(error, sitecoreContext.route?.displayName);

			return;
		}

		if (!!partyKey) {
			seatingData.PartyKey = partyKey;
			setSeatingData(seatingData);

			const guestsAndSelectedTime = handleFormatGuestsAndTimeSelections(seatingData.Guests, seatingData.Datetime);
			GaEvent.GetOnTheListSubmitButton(Number(seatingData.LocationId), guestsAndSelectedTime);
			onSuccess();
		}
	};

	const handleTimeSlotChange = (value: string) => {
		if (!value) return;

		seatingData.TimeSlot = value;
		setUnavailableTimeSlot(false);
		setTimeSlots([]);
		timeSlot.setValue('');
		handleConfirm();
	};

	const formatTimeSlot = () => {
		return format(seatingData.Datetime, 'h:mm aaa');
	};

	return (
		<>
			<Styled.GuestInfoContainer>
				<Holder>
					<h1>
						{seatingData.Guests} guests, plan to arrive today by {formatTimeSlot()}
					</h1>
					<Styled.Location>
						<div>
							<Icon icon={IconMapPin} customSize />
						</div>
						<span>{seatingData.LocationName}</span>
					</Styled.Location>

					<Styled.Grid columns='1fr 1fr 1fr'>
						<InputCms handler={firstName} variant='terciary' />
						<InputCms handler={lastName} variant='terciary' />
						<InputCms handler={phoneNumber} variant='terciary' mode={CmsInputModes.Numeric} />
					</Styled.Grid>

					<Styled.Grid columns='2fr 1fr'>
						<InputCms handler={email} variant='terciary' mode={CmsInputModes.Email} />
						<CmsDropdown handler={occasion} items={occasionOptions} />
					</Styled.Grid>

					<Styled.Info>
						<Checkbox
							label='Yes, I am at least 18 years of age. Sign me up for exclusive emails.'
							checked={checkbox}
							onClick={() => setCheckbox(!checkbox)}
						/>
					</Styled.Info>

					<Styled.Buttons>
						<Styled.Button secondary onClick={onBackClick}>
							Go back
						</Styled.Button>
						<Styled.Button onClick={handleConfirm}>Confirm</Styled.Button>
					</Styled.Buttons>
				</Holder>
			</Styled.GuestInfoContainer>

			{error && (
				<SeatingModal>
					<h2>Error</h2>
					<p>Wait list service is unavailable at the moment. Please try again at a later time.</p>
					<Styled.Button onClick={() => router.push('/')}>Ok</Styled.Button>
				</SeatingModal>
			)}

			{unavailableTimeSlot && (
				<SeatingModal>
					<h2>
						The time chosen is no longer available, please select another time.
						<br />
						What time?
					</h2>
					<section>
						<CmsDropdown items={timeSlots} handler={timeSlot} onChange={handleTimeSlotChange} />
					</section>
				</SeatingModal>
			)}

			{!!submitError && (
				<SeatingModal>
					<h2>Error</h2>
					<p>{submitError}</p>
					<Styled.Button onClick={() => setSubmitError('')}>Ok</Styled.Button>
				</SeatingModal>
			)}
		</>
	);
};
